interface AttentionSectionApiResource<ApiItem> {
  total: number;
  items: ApiItem[];
}

interface AttentionIntakeFormApiResource {
  submission_id: string;
  client_name: string;
  pet_name: string | null;
  submitted_at: string | null;
}

interface AttentionVisitApiResource {
  appointment_id: string;
  patient_id: string;
  patient_name: string;
  client_name: string;
  date: string;
  start_time: string;
  reschedule_request_message: string | null;
}

interface AttentionFileApiResource {
  file_id: string;
  patient_id: string;
  patient_name: string;
  filename: string;
  uploaded_at: string;
}

interface AttentionConsentApiResource {
  client_id: string;
  client_name: string;
  consent_title: string;
  withdrawn_at: string;
}

interface AttentionPetChangeRequestApiResource {
  request_id: string;
  patient_id: string;
  patient_name: string;
  client_name: string;
  requested_at: string;
}

export interface WorkspaceAttentionApiResource {
  intake_forms: AttentionSectionApiResource<AttentionIntakeFormApiResource> | null;
  reschedule_requests: AttentionSectionApiResource<AttentionVisitApiResource>;
  client_cancellations: AttentionSectionApiResource<AttentionVisitApiResource>;
  visits_awaiting_outcome: AttentionSectionApiResource<AttentionVisitApiResource>;
  files_awaiting_approval: AttentionSectionApiResource<AttentionFileApiResource> | null;
  withdrawn_consents: AttentionSectionApiResource<AttentionConsentApiResource> | null;
  pet_change_requests: AttentionSectionApiResource<AttentionPetChangeRequestApiResource>;
}

export interface AttentionSection<DomainItem> {
  total: number;
  items: DomainItem[];
}

export interface AttentionIntakeForm {
  submissionId: string;
  clientName: string;
  petName: string | null;
  submittedAt: string | null;
}

export interface AttentionVisit {
  appointmentId: string;
  patientId: string;
  patientName: string;
  clientName: string;
  date: string;
  startTime: string;
  rescheduleRequestMessage: string | null;
}

export interface AttentionFile {
  fileId: string;
  patientId: string;
  patientName: string;
  filename: string;
  uploadedAt: string;
}

export interface AttentionConsent {
  clientId: string;
  clientName: string;
  consentTitle: string;
  withdrawnAt: string;
}

export interface AttentionPetChangeRequest {
  requestId: string;
  patientId: string;
  patientName: string;
  clientName: string;
  requestedAt: string;
}

export interface WorkspaceAttention {
  intakeForms: AttentionSection<AttentionIntakeForm> | null;
  rescheduleRequests: AttentionSection<AttentionVisit>;
  clientCancellations: AttentionSection<AttentionVisit>;
  visitsAwaitingOutcome: AttentionSection<AttentionVisit>;
  filesAwaitingApproval: AttentionSection<AttentionFile> | null;
  withdrawnConsents: AttentionSection<AttentionConsent> | null;
  petChangeRequests: AttentionSection<AttentionPetChangeRequest>;
}

function mapSection<ApiItem, DomainItem>(
  section: AttentionSectionApiResource<ApiItem>,
  mapItem: (item: ApiItem) => DomainItem
): AttentionSection<DomainItem> {
  return { total: section.total, items: section.items.map(mapItem) };
}

function mapOptionalSection<ApiItem, DomainItem>(
  section: AttentionSectionApiResource<ApiItem> | null,
  mapItem: (item: ApiItem) => DomainItem
): AttentionSection<DomainItem> | null {
  return section === null ? null : mapSection(section, mapItem);
}

function mapVisit(item: AttentionVisitApiResource): AttentionVisit {
  return {
    appointmentId: item.appointment_id,
    patientId: item.patient_id,
    patientName: item.patient_name,
    clientName: item.client_name,
    date: item.date,
    startTime: item.start_time,
    rescheduleRequestMessage: item.reschedule_request_message,
  };
}

export function mapApiResourceToWorkspaceAttention(
  resource: WorkspaceAttentionApiResource
): WorkspaceAttention {
  return {
    intakeForms: mapOptionalSection(resource.intake_forms, function mapForm(item) {
      return {
        submissionId: item.submission_id,
        clientName: item.client_name,
        petName: item.pet_name,
        submittedAt: item.submitted_at,
      };
    }),
    rescheduleRequests: mapSection(resource.reschedule_requests, mapVisit),
    clientCancellations: mapSection(resource.client_cancellations, mapVisit),
    visitsAwaitingOutcome: mapSection(resource.visits_awaiting_outcome, mapVisit),
    filesAwaitingApproval: mapOptionalSection(
      resource.files_awaiting_approval,
      function mapFile(item) {
        return {
          fileId: item.file_id,
          patientId: item.patient_id,
          patientName: item.patient_name,
          filename: item.filename,
          uploadedAt: item.uploaded_at,
        };
      }
    ),
    withdrawnConsents: mapOptionalSection(resource.withdrawn_consents, function mapConsent(item) {
      return {
        clientId: item.client_id,
        clientName: item.client_name,
        consentTitle: item.consent_title,
        withdrawnAt: item.withdrawn_at,
      };
    }),
    petChangeRequests: mapSection(resource.pet_change_requests, function mapPetChangeRequest(item) {
      return {
        requestId: item.request_id,
        patientId: item.patient_id,
        patientName: item.patient_name,
        clientName: item.client_name,
        requestedAt: item.requested_at,
      };
    }),
  };
}

export function countAttentionItems(attention: WorkspaceAttention): number {
  const optionalTotals = [
    attention.intakeForms,
    attention.filesAwaitingApproval,
    attention.withdrawnConsents,
  ]
    .map(function toTotal(section) {
      return section === null ? 0 : section.total;
    })
    .reduce(function sum(total, value) {
      return total + value;
    }, 0);
  return (
    optionalTotals +
    attention.rescheduleRequests.total +
    attention.clientCancellations.total +
    attention.visitsAwaitingOutcome.total +
    attention.petChangeRequests.total
  );
}
